import { Controller } from '@hotwired/stimulus';

// "Toggle all" masters in the Goals v3 card header.
//
// Four buttons (Details / Status history / Goal progress / Comments), one
// active at a time. Clicking a master expands EVERY visible goal row on the
// page (LTGs and STGs) with only that panel open; clicking the active
// master again collapses every row; clicking a different master switches
// every row to the new panel. The active master reads as open (aria-expanded).
//
// Rows are driven through their own goals-v3-row controllers, which apply
// the state synchronously (no animation), so a page-wide sweep can never be
// half-dropped by Bootstrap's in-transition guard. A sweep is skipped while
// anything inside a row is still animating from a per-row click. Opening
// leaves rows hidden by the per-LTG STG filter alone (they would otherwise
// reappear already expanded when the filter changes); closing sweeps every
// row so nothing hidden stays open behind the filter.
//
// A second, panel-less entry point serves the filter strip's twisty
// (toggleAllRows): any visible row closed -> expand every visible row with
// no panel forced; otherwise collapse every row. The twisty mounts its own
// instance of this controller (rows are found document-wide), and either
// entry point resets the other's open state so the masters and the caret
// never disagree about what just happened.
//
// Targets:
//   button    - each master button (data-...-panel-param = details|status|progress|comments)
//   allToggle - the expand/collapse-all twisty button (optional)
// Values:
//   active - the panel key currently applied page-wide ('' when none)
export default class extends Controller {
  static targets = ['button', 'allToggle'];
  static values = { active: { type: String, default: '' } };

  toggle(event) {
    const { panel } = event.params;
    if (!panel) return;
    if (document.querySelector('.gv3-expand-detail.collapsing, .gv3-expand-detail .collapsing')) return;

    const closing = this.activeValue === panel;
    this.activeValue = closing ? '' : panel;

    this.rows(closing).forEach((row) => {
      if (closing) {
        row.close();
      } else {
        row.open(panel);
      }
    });

    this.buttonTargets.forEach((button) => {
      const isActive = !closing && button.dataset.goalsV3MasterTogglePanelParam === panel;
      button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
    this.eachInstance((instance) => instance.syncAllToggles(!closing));
  }

  // The filter strip's twisty: expand every visible row (no panel forced —
  // rows open to their header state) when any is closed, collapse every row
  // otherwise. Same in-transition guard and hidden-row semantics as the
  // masters, and a fresh sweep clears any active master, since the panels
  // it applied are gone.
  toggleAllRows() {
    if (document.querySelector('.gv3-expand-detail.collapsing, .gv3-expand-detail .collapsing')) return;

    const opening = this.rows(false).some((row) => !row.isOpen);
    this.rows(!opening).forEach((row) => {
      if (opening) {
        row.open(undefined);
      } else {
        row.close();
      }
    });

    this.eachInstance((instance) => instance.clearActive());
    this.eachInstance((instance) => instance.syncAllToggles(opening));
  }

  clearActive() {
    this.activeValue = '';
    this.buttonTargets.forEach((button) => button.setAttribute('aria-expanded', 'false'));
  }

  // The active master, applied again to rows that arrived after it was
  // clicked (the filter swap brings in the roster with every panel closed),
  // so the master and the rows agree: every visible row open with its panel.
  reapply() {
    const panel = this.activeValue;
    if (!panel) return;
    const rows = this.rows(false);
    rows.forEach((row) => row.open(panel));
    this.eachInstance((instance) => instance.syncAllToggles(rows.length > 0));
  }

  // Caret state on every mounted twisty (this instance's and any other's).
  syncAllToggles(open) {
    this.allToggleTargets.forEach((toggleButton) => {
      toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      const caret = toggleButton.querySelector('.gv3-expand-caret');
      if (caret) caret.classList.toggle('is-open', open);
    });
  }

  eachInstance(fn) {
    document.querySelectorAll('[data-controller~="goals-v3-master-toggle"]').forEach((element) => {
      const instance = this.application.getControllerForElementAndIdentifier(element, 'goals-v3-master-toggle');
      if (instance) fn(instance);
    });
  }

  // Every mounted goals-v3-row controller; when opening, only those whose
  // card is currently shown. A card hidden by the per-goal STG filter carries
  // d-none, written by the server when it seeds the card and by the filter
  // controller on every toggle. The inline-display check stays for anything
  // else on the page that hides a row that way.
  rows(includeHidden) {
    const shown = (element) =>
      element.style.display !== 'none' && !element.classList.contains('d-none') && !element.closest('[hidden]');
    return Array.from(document.querySelectorAll('[data-controller~="goals-v3-row"]'))
      .filter((element) => includeHidden || shown(element))
      .map((element) => this.application.getControllerForElementAndIdentifier(element, 'goals-v3-row'))
      .filter(Boolean);
  }
}
